import React, { useEffect, useRef, useState } from 'react';
import ModalForms from '../../../Shared/Modals/index.js';
import { Box, Button, TextField, Typography, MenuItem, Grid } from '@mui/material';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ReportActions } from '../../../../Services/_actions/Report/report.action.js';
import { reportTemplateDetailsAtom } from '../../../../Services/_states/Report/index.ts';
import { campaignLeadManagementAtom } from '../../../../Services/_states/index.js';
import { LeadFormActions } from '../../../../Services/_actions/index.js';

interface TriggerModalProps {
  show: boolean;
  handleClose: () => void;
  node: any;
  // onChange: (updatedNode: any) => void;
}

type FilterRow = {
  field_name: string;
  operator: string;
  value: any;
  data_type: 'string' | 'number' | 'date';
};

type GroupingRow =
  | {
      group_type: 'date_part';
      field_name: string;
      display_name?: string;
      date_part: 'year' | 'month' | 'day' | 'hour' | 'date';
    }
  | { group_type: 'field'; field_name: string; display_name?: string }
  | {
      group_type: 'array_element';
      field_name: string;
      display_name?: string;
      array_key_field: string;
      array_value_field: string;
      array_key_values: string[];
    };

type AggregationRow = {
  field_name: string;
  function: string;
  display_name?: string;
  output_field?: string;
};

type SortingRow = {
  field_name: string;
  direction: 'asc' | 'desc';
};

type FormData = {
  id: string;
  title: string;
  type: string;
  collection: string;
  filters: FilterRow[];
  grouping: GroupingRow[];
  aggregations: AggregationRow[];
  sorting: SortingRow[];
};

const parseMaybeJSON = <T,>(v: any, fallback: T): T => {
  try {
    if (v == null || v === '') return fallback;
    if (typeof v === 'string') return JSON.parse(v) as T;
    if (typeof v === 'object') return v as T;
    return fallback;
  } catch {
    return fallback;
  }
};

const TriggerModal: React.FC<TriggerModalProps> = ({ show, handleClose, node }) => {
  const { showSnackBar } = useSnackBar();
  const [saving, setSaving] = useState(false);
  const [chartData, setChartData] = useRecoilState<any>(reportTemplateDetailsAtom);
  const actions = useRef(ReportActions()).current;
  const action = LeadFormActions();
  const [schema, setSchema] = useState<any>(null);
  const [statusList, setStatusList] = useState<any>([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [typeOfStatusList, setTypeOfStatusList] = useState<any>({});
  console.log('typeOfStatusList', typeOfStatusList);

  const [formData, setFormData] = useState<FormData>({
    id: '',
    title: '',
    type: 'bar',
    collection: 'leads',
    filters: [],
    grouping: [],
    aggregations: [],
    sorting: [],
  });

  const getStatusList = async () => {
    setIsStatusLoading(true);
    try {
      if (statusList?.length > 0) return;
      if (!chartData?.leads_form_id) return;

      const res = await action.fetchStatusAndSubStatus({
        leadFormId: chartData.leads_form_id,
        active: true,
      });

      if (res?.status === 200) {
        const allStatuses = res?.data || [];
        setStatusList(allStatuses);

        const statusData: any = {};
        allStatuses.forEach((item: any) => {
          if (item?.type_of_status) {
            if (!statusData[item.type_of_status]) statusData[item.type_of_status] = [];
            statusData[item.type_of_status].push(item);
          }
          item?.substatuses?.forEach((sub: any) => {
            const key = sub?.type_of_status || 'unknown';
            if (!statusData[key]) statusData[key] = [];
            statusData[key].push(sub);
          });
        });
        setTypeOfStatusList(statusData);
      }
    } catch (error) {
      console.warn('Error fetching status/substatus:', error);
    } finally {
      setIsStatusLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      getStatusList();
    }
  }, [show, chartData?.leads_form_id]);

  const fetchSchema = async (formId: string) => {
    try {
      const res = await actions.getSchemaHelper({
        collection: 'leads_forms',
        leads_form_id: formId,
      });
      if (res?.data?.status) {
        setSchema(res?.data?.data ?? {});
      }
    } catch (err) {
      console.error('Error fetching schema:', err);
    }
  };

  // Initialize form data with node data
  useEffect(() => {
    if (!show || !node) return;

    // Find existing chart from chartData
    const existingChart = Array.isArray(chartData?.charts)
      ? chartData.charts.find((c: any) => c.id === node.id)
      : null;

    // Set form data from existing chart or node data
    const initialData = existingChart || node;

    setFormData({
      id: initialData.id || `chart_${Date.now()}`,
      title: initialData.title || 'Untitled Chart',
      type: initialData.type || 'bar',
      collection: initialData.collection || initialData.group || 'leads',
      filters: parseMaybeJSON<FilterRow[]>(initialData.filters || initialData.filter, []),
      grouping: parseMaybeJSON<GroupingRow[]>(initialData.grouping || initialData.options, []),
      aggregations: parseMaybeJSON<AggregationRow[]>(
        initialData.aggregations || initialData.aggregation,
        []
      ),
      sorting: parseMaybeJSON<SortingRow[]>(initialData.sorting, []),
    });
  }, [show, node, chartData]);

  // Fetch schema when modal opens
  useEffect(() => {
    if (!show) return;
    if (!chartData?.leads_form_id) return;
    fetchSchema(chartData.leads_form_id);
  }, [show, chartData?.leads_form_id]);

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const chartObj = {
        ...formData,
        columns: [],
        limit: 1000,
      };
      setChartData((prevChartData) => {
        const existingCharts = Array.isArray(prevChartData?.charts) ? prevChartData.charts : [];
        const chartExists = existingCharts.some((c: any) => c.id === formData.id);

        const updatedCharts = chartExists
          ? existingCharts.map((c: any) => (c.id === formData.id ? chartObj : c))
          : [...existingCharts, chartObj];

        return {
          ...prevChartData,
          charts: updatedCharts,
        };
      });
      showSnackBar('Chart saved successfully!', 'success');
      handleClose();
    } catch (err) {
      console.error('Error saving chart:', err);
      showSnackBar('Error saving chart', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const schemaFieldOptions = (schema?.fields || []).map((sf: any) => ({
    label: sf.display_name || sf.field_name,
    value: sf.field_name,
    data_type: String(sf.data_type || 'STRING').toLowerCase(),
    sample_values: sf.sample_values || [],
  }));

  const functionOptions = (schema?.available_functions || []).map((f: any) => ({
    label: f.display_name || f.function,
    value: f.function,
  }));

  const groupTypeOptions = (schema?.grouping_types || []).map((f: any) => ({
    label: f.display_name || f.group_type,
    value: f.group_type,
  }));

  const getStatusOptions = (fieldName?: string) => {
    if (!fieldName) return [];
    const key = fieldName.includes('.') ? fieldName.split('.').pop() : fieldName;
    return typeOfStatusList?.[key] ?? [];
  };

  const getSubStatusOptions = (fieldName: string) => {
    if (!fieldName) return [];

    // Get the key from fieldName
    const key = fieldName.includes('.') ? fieldName.split('.').pop()! : fieldName;

    // Get all items for this type
    const allStatuses = typeOfStatusList?.[key] ?? [];

    // Filter only sub-statuses
    const subStatuses = allStatuses.filter((item: any) => item.is_sub_status || item.sub_status_id);

    console.log(`Sub-status options for field "${fieldName}" (key="${key}"):`, subStatuses);

    return subStatuses;
  };

  // Filter management functions
  const addFilter = () => {
    updateFormData({
      filters: [
        ...formData.filters,
        { field_name: '', operator: '', value: '', data_type: 'string' },
      ],
    });
  };

  const removeFilter = (index: number) => {
    updateFormData({
      filters: formData.filters.filter((_, i) => i !== index),
    });
  };

  const updateFilter = (index: number, field: keyof FilterRow, value: any) => {
  const updatedFilters = formData.filters.map((row, i) => {
    if (i !== index) return row;
    const updated = { ...row, [field]: value } as any;

    // ✅ If field_name changed, check its data_type in schema
    if (field === 'field_name') {
      const fieldDef = schemaFieldOptions.find((sf: any) => sf.value === value);

      if (fieldDef?.data_type?.toUpperCase() === 'STATUS') {
        updated.field_type = 'status';
      } else if (fieldDef?.data_type?.toUpperCase() === 'SUB_STATUS') {
        updated.field_type = 'substatus';
      } else {
        delete updated.field_type;
      }
    }

    return updated;
  });

  updateFormData({ filters: updatedFilters });
};


  // const updateFilter = (index: number, field: keyof FilterRow, value: any) => {
  //   const updatedFilters = formData.filters.map((row, i) =>
  //     i === index ? { ...row, [field]: value } : row
  //   );
  //   updateFormData({ filters: updatedFilters });
  // };

  // Grouping management functions
  const addGrouping = () => {
    updateFormData({
      grouping: [
        ...formData.grouping,
        { group_type: 'field', field_name: '', display_name: '' } as GroupingRow,
      ],
    });
  };

  const removeGrouping = (index: number) => {
    updateFormData({
      grouping: formData.grouping.filter((_, i) => i !== index),
    });
  };

  const updateGrouping = (index: number, updates: Partial<GroupingRow>) => {
  const updatedGrouping = formData.grouping.map((row, i) => {
    if (i !== index) return row;
    const updated = { ...row, ...updates } as any;

    // ✅ Check data_type of the field
    const fieldName = updates.field_name ?? row.field_name;
    const fieldDef = schemaFieldOptions.find((sf: any) => sf.value === fieldName);

    if (fieldDef?.data_type?.toUpperCase() === 'STATUS') {
      updated.field_type = 'status';
    } else if (fieldDef?.data_type?.toUpperCase() === 'SUB_STATUS') {
      updated.field_type = 'substatus';
    } else {
      delete updated.field_type;
    }

    return updated;
  });

  updateFormData({ grouping: updatedGrouping });
};

  // const updateGrouping = (index: number, updates: Partial<GroupingRow>) => {
  //   const updatedGrouping = formData.grouping.map((row, i) =>
  //     i === index ? ({ ...row, ...updates } as GroupingRow) : row
  //   );
  //   updateFormData({ grouping: updatedGrouping });
  // };

  // Aggregation management functions
  const addAggregation = () => {
    updateFormData({
      aggregations: [
        ...formData.aggregations,
        { field_name: '', function: '', display_name: '', output_field: '' },
      ],
    });
  };

  const removeAggregation = (index: number) => {
    updateFormData({
      aggregations: formData.aggregations.filter((_, i) => i !== index),
    });
  };

//   const updateAggregation = (index: number, field: keyof AggregationRow, value: any) => {
//   const updatedAggregations = formData.aggregations.map((row, i) => {
//     if (i !== index) return row;
//     const updated = { ...row, [field]: value } as any;

//     if (field === 'field_name') {
//       // ✅ Check data_type of the field
//       const fieldDef = schemaFieldOptions.find((sf: any) => sf.value === value);
//       if (fieldDef?.data_type?.toUpperCase() === 'STATUS') {
//         updated.field_type = 'status';
//       } else if (fieldDef?.data_type?.toUpperCase() === 'SUB_STATUS') {
//         updated.field_type = 'substatus';
//       } else {
//         delete updated.field_type;
//       }
//     }

//     return updated;
//   });

//   updateFormData({ aggregations: updatedAggregations });
// };


  const updateAggregation = (index: number, field: keyof AggregationRow, value: any) => {
    const updatedAggregations = formData.aggregations.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    updateFormData({ aggregations: updatedAggregations });
  };

  // Sorting management functions
  const addSorting = () => {
    updateFormData({
      sorting: [...formData.sorting, { field_name: '', direction: 'asc' }],
    });
  };

  const removeSorting = (index: number) => {
    updateFormData({
      sorting: formData.sorting.filter((_, i) => i !== index),
    });
  };

  const updateSorting = (index: number, field: keyof SortingRow, value: any) => {
    const updatedSorting = formData.sorting.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    updateFormData({ sorting: updatedSorting });
  };

  return (
    <ModalForms
      show={show}
      handleClose={handleClose}
      modalHeader="Chart Configuration"
      modalBody={
        <Box sx={{ p: 2, display: 'grid', gap: 2 }}>
          {/* Basic Chart Information */}
          <TextField
            fullWidth
            size="small"
            label="Chart Title"
            value={formData?.title ?? ''}
            onChange={(e) => updateFormData({ title: e.target.value })}
          />

          {/* <TextField
            select
            fullWidth
            size="small"
            label="Chart Type"
            value={formData?.type ?? "bar"}
            onChange={(e) => updateFormData({ type: e.target.value })}
          >
            <MenuItem value="bar">Bar Chart</MenuItem>
            <MenuItem value="line">Line Chart</MenuItem>
            <MenuItem value="pie">Pie Chart</MenuItem>
            <MenuItem value="table">Table</MenuItem>
          </TextField> */}

          <TextField
            fullWidth
            size="small"
            label="Collection"
            value={formData.collection}
            disabled
          />

          {/* Filters Section */}
          <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2">Filters</Typography>
            <Box mt={1} display="grid" gap={1}>
              {formData?.filters?.map((filter, i) => (
                <Grid container spacing={1} alignItems="center" key={i}>
                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Field"
                      value={filter.field_name}
                      // onChange={(e) => updateFilter(i, 'field_name', e.target.value)}
                      onChange={(e) => {
  const newField = e.target.value;
  updateFilter(i, 'field_name', newField);
}}

                      // onChange={(e) => {
                      //   const newField = e.target.value;
                      //   const updatedFilters = formData.filters.map((row, idx) =>
                      //     idx === i
                      //       ? {
                      //           ...row,
                      //           field_name: newField,
                      //           value: '',
                      //         }
                      //       : row
                      //   );

                      //   updateFormData({ filters: updatedFilters });
                      // }}
                    >
                      <MenuItem value="">Select field</MenuItem>
                      {schemaFieldOptions.map((sf: any) => (
                        <MenuItem key={sf.value} value={sf.value}>
                          {sf.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Operator"
                      value={filter.operator}
                      onChange={(e) => updateFilter(i, 'operator', e.target.value)}
                    >
                      <MenuItem value="">Select operator</MenuItem>
                      {(schema?.available_operators || []).map((op: any) => (
                        <MenuItem key={op.operator} value={op.operator}>
                          {op.display_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === filter.field_name
                    );
                    const sampleValues = fieldDef?.sample_values || [];

                    if (sampleValues.length === 0) return null;

                    const multiOperators = ['in', 'nin'];
                    const isMulti = multiOperators.includes((filter.operator ?? '').toLowerCase());

                    return (
                      <Grid item xs={3}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Sample Values"
                          SelectProps={{
                            multiple: isMulti,
                            renderValue: (selected: any) =>
                              Array.isArray(selected) ? selected.join(', ') : (selected ?? ''),
                          }}
                          value={
                            isMulti
                              ? Array.isArray(filter.value)
                                ? filter.value
                                : []
                              : (filter.value ?? '')
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            updateFilter(i, 'value', val);
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {sampleValues.map((sv: string, idx: number) => (
                            <MenuItem key={idx} value={sv}>
                              {isMulti ? (
                                <>
                                  <input
                                    type="checkbox"
                                    checked={
                                      Array.isArray(filter.value) && filter.value.includes(sv)
                                    }
                                    readOnly
                                    style={{ marginRight: 8 }}
                                  />
                                  {sv}
                                </>
                              ) : (
                                sv
                              )}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    );
                  })()}
                  {/* {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === filter.field_name
                    );

                    if (!fieldDef) return null;
                    const dataType = fieldDef.data_type?.toUpperCase();

                    if (dataType === 'STATUS') {
                      // Pull options directly from typeOfStatusList
                      const statusOptions = getStatusOptions(filter.field_name);

                      return (
                        <Grid item xs={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            value={filter.value ?? ''}
                            onChange={(e) => updateFilter(i, 'value', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select status</em>
                            </MenuItem>

                            {statusOptions.map((status: any) => (
                              <MenuItem key={status.status_id} value={status.status_id}>
                                {status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }
                  })()} */}
                  {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === filter.field_name
                    );

                    if (!fieldDef) return null;
                    const dataType = fieldDef.data_type?.toUpperCase();

                    if (dataType === 'STATUS') {
                      // Pull options directly from typeOfStatusList
                      const statusOptions = getStatusOptions(filter.field_name);
                      const multiOperators = ['in', 'nin'];
                      const isMulti = multiOperators.includes(
                        (filter.operator ?? '').toLowerCase()
                      );

                      // Find selected status objects for display
                      let selectedStatuses: any[] = [];
                      if (isMulti && Array.isArray(filter.value)) {
                        selectedStatuses = statusOptions.filter((status: any) =>
                          filter.value.includes(status.status_id)
                        );
                      } else if (!isMulti && filter.value) {
                        const found = statusOptions.find(
                          (status: any) => status.status_id === filter.value
                        );
                        if (found) selectedStatuses = [found];
                      }

                      return (
                        <Grid item xs={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            SelectProps={{
                              multiple: isMulti,
                              renderValue: (selected: any) => {
                                if (isMulti) {
                                  // Show names for multi-select
                                  return statusOptions
                                    .filter((status: any) => selected.includes(status.status_id))
                                    .map((status: any) => status.status_name)
                                    .join(', ');
                                } else {
                                  // Show name for single select
                                  const found = statusOptions.find(
                                    (status: any) => status.status_id === selected
                                  );
                                  return found ? found.status_name : '';
                                }
                              },
                            }}
                            value={
                              isMulti
                                ? Array.isArray(filter.value)
                                  ? filter.value
                                  : []
                                : (filter.value ?? '')
                            }
                            onChange={(e) => updateFilter(i, 'value', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select status</em>
                            </MenuItem>

                            {statusOptions.map((status: any) => (
                              <MenuItem key={status.status_id} value={status.status_id}>
                                {status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }
                  })()}
                  {/* {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === filter.field_name
                    );

                    if (!fieldDef) return null;
                    const dataType = fieldDef.data_type?.toUpperCase();

                    if (dataType === 'SUB_STATUS') {
                      const statusOptions = getSubStatusOptions(filter.field_name);
                      console.log('Rendering sub-status options:', statusOptions);

                      return (
                        <Grid item xs={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Sub-Status"
                            value={filter.value ?? ''}
                            onChange={(e) => updateFilter(i, 'value', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select Substatus</em>
                            </MenuItem>

                            {statusOptions.map((status: any) => (
                              <MenuItem
                                key={status.sub_status_id || status.status_id}
                                value={status.sub_status_id || status.status_id}
                              >
                                {status.name || status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }
                  })()} */}
                  {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === filter.field_name
                    );

                    if (!fieldDef) return null;
                    const dataType = fieldDef.data_type?.toUpperCase();

                    if (dataType === 'SUB_STATUS') {
                      const subStatusOptions = getSubStatusOptions(filter.field_name);
                      const multiOperators = ['in', 'nin'];
                      const isMulti = multiOperators.includes(
                        (filter.operator ?? '').toLowerCase()
                      );

                      // Find selected sub-status objects for display
                      let selectedSubStatuses: any[] = [];
                      if (isMulti && Array.isArray(filter.value)) {
                        selectedSubStatuses = subStatusOptions.filter((status: any) =>
                          filter.value.includes(status.sub_status_id || status.status_id)
                        );
                      } else if (!isMulti && filter.value) {
                        const found = subStatusOptions.find(
                          (status: any) =>
                            (status.sub_status_id || status.status_id) === filter.value
                        );
                        if (found) selectedSubStatuses = [found];
                      }

                      return (
                        <Grid item xs={3}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Sub-Status"
                            SelectProps={{
                              multiple: isMulti,
                              renderValue: (selected: any) => {
                                if (isMulti) {
                                  return subStatusOptions
                                    .filter((status: any) =>
                                      selected.includes(status.sub_status_id || status.status_id)
                                    )
                                    .map((status: any) => status.name || status.status_name)
                                    .join(', ');
                                } else {
                                  const found = subStatusOptions.find(
                                    (status: any) =>
                                      (status.sub_status_id || status.status_id) === selected
                                  );
                                  return found ? found.name || found.status_name : '';
                                }
                              },
                            }}
                            value={
                              isMulti
                                ? Array.isArray(filter.value)
                                  ? filter.value
                                  : []
                                : (filter.value ?? '')
                            }
                            onChange={(e) => updateFilter(i, 'value', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select Substatus</em>
                            </MenuItem>

                            {subStatusOptions.map((status: any) => (
                              <MenuItem
                                key={status.sub_status_id || status.status_id}
                                value={status.sub_status_id || status.status_id}
                              >
                                {status.name || status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }
                  })()}

                  {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === filter.field_name
                    );
                    const dataType = fieldDef?.data_type?.toUpperCase();

                    if (dataType === 'STATUS' || dataType === 'SUB_STATUS') {
                      return null;
                    }

                    return (
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Value"
                          placeholder={
                            filter.data_type === 'date' ? 'YYYY-MM-DD or {{start_date}}' : ''
                          }
                          value={filter.value}
                          onChange={(e) => {
                            updateFilter(i, 'value', e.target.value);
                          }}
                        />
                      </Grid>
                    );
                  })()}

                  <Grid item xs={2}>
                    <Button size="small" onClick={() => removeFilter(i)}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}

              <Button size="small" onClick={addFilter}>
                + Add Filter
              </Button>
            </Box>
          </Box>

          {/* Grouping Section */}
          <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2">X Axis</Typography>
            {formData?.grouping?.map((group, i) => (
              <Box
                key={`${group.group_type}-${i}`}
                sx={{ mt: 1, p: 1, border: '1px dashed #ddd', borderRadius: 1 }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Group Type"
                      value={group.group_type}
                      onChange={(e) => {
                        const group_type = e.target.value as GroupingRow['group_type'];
                        let newGroup: GroupingRow;

                        if (group_type === 'date_part') {
                          newGroup = {
                            group_type: 'date_part',
                            field_name: group.field_name || 'created_at',
                            display_name: group.display_name || 'date',
                            date_part: 'date',
                          };
                        } else if (group_type === 'field') {
                          newGroup = {
                            group_type: 'field',
                            field_name: group.field_name || '',
                            display_name: group.display_name || '',
                          };
                        } else {
                          newGroup = {
                            group_type: 'array_element',
                            field_name: group.field_name || 'data',
                            display_name: group.display_name || '',
                            array_key_field: '',
                            array_value_field: '',
                            array_key_values: [],
                          };
                        }

                        updateGrouping(i, newGroup);
                      }}
                    >
                      <MenuItem value="">Select group type</MenuItem>
                      {groupTypeOptions.map((gt: any) => (
                        <MenuItem key={gt.value} value={gt.value}>
                          {gt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Field"
                      value={group.field_name}
                      onChange={(e) => updateGrouping(i, { field_name: e.target.value })}
                    >
                      <MenuItem value="">Select field</MenuItem>
                      {schemaFieldOptions.map((sf: any) => (
                        <MenuItem key={sf.value} value={sf.value}>
                          {sf.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Display Name"
                      value={group.display_name || ''}
                      onChange={(e) => updateGrouping(i, { display_name: e.target.value })}
                    />
                  </Grid>

                  {group.group_type === 'date_part' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Date Part"
                        value={(group as any).date_part || ''}
                        onChange={(e) => updateGrouping(i, { date_part: e.target.value })}
                      >
                        <MenuItem value="">Select date part</MenuItem>
                        {(
                          schema?.grouping_types?.find((gt: any) => gt.group_type === 'date_part')
                            ?.date_parts || []
                        ).map((dp: any) => (
                          <MenuItem key={dp.value} value={dp.value}>
                            {dp.display}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {group.group_type === 'array_element' && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Array Key Field"
                          value={(group as any).array_key_field || ''}
                          onChange={(e) => updateGrouping(i, { array_key_field: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Array Value Field"
                          value={(group as any).array_value_field || ''}
                          onChange={(e) => updateGrouping(i, { array_value_field: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Array Key Values (comma-separated)"
                          value={((group as any).array_key_values || []).join(', ')}
                          onChange={(e) =>
                            updateGrouping(i, {
                              array_key_values: e.target.value
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean),
                            })
                          }
                        />
                      </Grid>
                    </>
                  )}
                  {/* {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === group.field_name
                    );

                    if (!fieldDef) return null;
                    const dataType = fieldDef.data_type?.toUpperCase();

                    if (dataType === 'STATUS') {
                      const statusOptions = getStatusOptions(group.field_name);
                      return (
                        <Grid item xs={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Select Status"
                            value={(group as any).value || ''}
                            onChange={(e) => updateGrouping(i, { value: e.target.value })}
                          >
                            <MenuItem value="">
                              <em>Select status</em>
                            </MenuItem>
                            {statusOptions.map((status: any) => (
                              <MenuItem key={status.status_id} value={status.status_id}>
                                {status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }

                    // ✅ If grouping by SUB_STATUS field
                    if (dataType === 'SUB_STATUS') {
                      const subStatusOptions = getSubStatusOptions(group.field_name);
                      return (
                        <Grid item xs={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Select Sub-Status"
                            value={(group as any).value || ''}
                            onChange={(e) => updateGrouping(i, { value: e.target.value })}
                          >
                            <MenuItem value="">
                              <em>Select sub-status</em>
                            </MenuItem>
                            {subStatusOptions.map((status: any) => (
                              <MenuItem
                                key={status.sub_status_id || status.status_id}
                                value={status.sub_status_id || status.status_id}
                              >
                                {status.name || status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }

                    return null;
                  })()} */}

                  <Grid item xs={12}>
                    <Button size="small" onClick={() => removeGrouping(i)}>
                      Remove Grouping
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button size="small" sx={{ mt: 1 }} onClick={addGrouping}>
              + Add Grouping
            </Button>
          </Box>

          {/* Aggregations Section */}
          <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2">Y Axis</Typography>
            <Box mt={1} display="grid" gap={1}>
              {formData.aggregations.map((agg, i) => (
                <Grid container spacing={1} alignItems="center" key={i}>
                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Field"
                      value={agg?.field_name ?? ''}
                      onChange={(e) => updateAggregation(i, 'field_name', e.target.value)}
                    >
                      <MenuItem value="">Select field</MenuItem>
                      {schemaFieldOptions.map((sf: any) => (
                        <MenuItem key={sf.value} value={sf.value}>
                          {sf.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Function"
                      value={agg.function}
                      onChange={(e) => updateAggregation(i, 'function', e.target.value)}
                    >
                      <MenuItem value="">Select function</MenuItem>
                      {functionOptions.map((fo: any) => (
                        <MenuItem key={fo.value} value={fo.value}>
                          {fo.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Display Name"
                      value={agg.display_name || ''}
                      onChange={(e) => updateAggregation(i, 'display_name', e.target.value)}
                    />
                  </Grid>

                  {/* <Grid item xs={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Output Field"
                      value={agg.output_field || ''}
                      onChange={(e) => updateAggregation(i, 'output_field', e.target.value)}
                    />
                  </Grid> */}
                  {/* {(() => {
                    const fieldDef = schemaFieldOptions.find(
                      (sf: any) => sf.value === agg.field_name
                    );

                    if (!fieldDef) return null;
                    const dataType = fieldDef.data_type?.toUpperCase();

                    // ✅ If grouping by STATUS field
                    if (dataType === 'STATUS') {
                      const statusOptions = getStatusOptions(agg.field_name);
                      return (
                        <Grid item xs={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Select Status"
                            value={(agg as any).value || ''}
                            onChange={(e) => updateAggregation(i, 'value', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select status</em>
                            </MenuItem>
                            {statusOptions.map((status: any) => (
                              <MenuItem key={status.status_id} value={status.status_id}>
                                {status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }

                    // ✅ If grouping by SUB_STATUS field
                    if (dataType === 'SUB_STATUS') {
                      const subStatusOptions = getSubStatusOptions(agg.field_name);
                      return (
                        <Grid item xs={4}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Select Sub-Status"
                            value={(agg as any).value || ''}
                            onChange={(e) => updateAggregation(i, 'value', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select sub-status</em>
                            </MenuItem>
                            {subStatusOptions.map((status: any) => (
                              <MenuItem
                                key={status.sub_status_id || status.status_id}
                                value={status.sub_status_id || status.status_id}
                              >
                                {status.name || status.status_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      );
                    }

                    return null;
                  })()} */}

                  <Grid item xs={2}>
                    <Button size="small" onClick={() => removeAggregation(i)}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}

              <Button size="small" onClick={addAggregation}>
                + Add Aggregation
              </Button>
            </Box>
          </Box>

          {/* Sorting Section */}
          <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2">Sorting</Typography>
            <Box mt={1} display="grid" gap={1}>
              {formData.sorting.map((sort, i) => (
                <Grid container spacing={1} alignItems="center" key={i}>
                  <Grid item xs={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Field"
                      value={sort.field_name}
                      onChange={(e) => updateSorting(i, 'field_name', e.target.value)}
                    >
                      <MenuItem value="">Select field</MenuItem>
                      {schemaFieldOptions.map((sf: any) => (
                        <MenuItem key={sf.value} value={sf.value}>
                          {sf.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Direction"
                      value={sort.direction}
                      onChange={(e) => updateSorting(i, 'direction', e.target.value)}
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={4}>
                    <Button size="small" onClick={() => removeSorting(i)}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}

              <Button size="small" onClick={addSorting}>
                + Add Sorting
              </Button>
            </Box>
          </Box>
        </Box>
      }
      size="lg"
      modalFooter={
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      }
    />
  );
};

export default TriggerModal;
